import React from "react";
import { withRouter } from "react-router-dom";
import { AppSettings } from "./../../config/app-settings.js";
import SidebarNavList from "./sidebar-nav-list.jsx";
import menus, {
  COMPANY_MENU,
  MANAGED_OWNER_MENU,
} from "./menu.jsx";
import { menuMatchesLocation } from "./menuMatch.js";
import {
  clearAuthData,
  isAnglerOnlyUser,
  isCompanyManagedBillingUser,
  isCompanyManagedSamplingOnlyUser,
  isCompanyManagerUser,
  isFisherOwnerUser,
  isIndependentScientistUser,
  isSuperUserSession,
} from "../content/axiosConfig.js";
import {
  clearStashedCompanySession,
  hasStashedCompanySession,
  returnToCompanySession,
} from "../../utils/companyHandoff.js";
import {
  clearScopedAccount,
  getScopedAccount,
  isActingAsScopedAccount,
} from "../../utils/accountContext.js";

const SECTIONS = [
  { key: "menu", label: "Menu" },
  { key: "general", label: "General" },
];

function menusVisibleForUser() {
  // Company without an open account → company ops menu
  if (isCompanyManagerUser() && !isActingAsScopedAccount()) {
    return COMPANY_MENU.map((m) => ({ ...m, search: false }));
  }

  // Company/Super with scoped account → full owner Admin UI for that account
  if (
    (isCompanyManagerUser() || isSuperUserSession()) &&
    isActingAsScopedAccount()
  ) {
    const base = menus.map((m) => ({ ...m, search: false }));
    // Super can accept/send consultancy invites for the opened pond owner.
    // Company managers are already attached to managed accounts — hide join.
    const hide = new Set(["/scientistInvites", "/packages"]);
    if (isCompanyManagerUser() && !isSuperUserSession()) {
      hide.add("/join-consultancy");
    }
    return base.filter((item) => !hide.has(item.path));
  }

  // Company-managed owners who self-login → sampling only
  if (isCompanyManagedSamplingOnlyUser()) {
    return MANAGED_OWNER_MENU.map((m) => ({ ...m, search: false }));
  }

  const base = menus.map((m) => ({ ...m, search: false }));
  if (isIndependentScientistUser()) {
    const hide = new Set(["/staff", "/packages", "/join-consultancy"]);
    return base.filter((item) => !hide.has(item.path));
  }
  let withoutScientistInvites = base.filter((item) => item.path !== "/scientistInvites");
  if (isCompanyManagedBillingUser()) {
    withoutScientistInvites = withoutScientistInvites.filter(
      (item) => item.path !== "/packages" && item.path !== "/join-consultancy"
    );
  }
  // Join consultancy is for independent pond owners only
  if (!isFisherOwnerUser() || isCompanyManagerUser()) {
    withoutScientistInvites = withoutScientistInvites.filter(
      (item) => item.path !== "/join-consultancy"
    );
  }
  if (!isAnglerOnlyUser()) return withoutScientistInvites;
  const hide = new Set([
    "/staff",
    "/packages",
    "/FishDataTableView",
    "/fish-data-graph-new",
    "/join-consultancy",
  ]);
  return withoutScientistInvites.filter((item) => !hide.has(item.path));
}

class SidebarNav extends React.Component {
  static contextType = AppSettings;

  constructor(props) {
    super(props);
    this.baseMenus = menusVisibleForUser();
    this.state = {
      active: -1,
      clicked: -1,
      menus: this.baseMenus,
      scopedAccount: getScopedAccount(),
    };

    this.handleSidebarSearch = this.handleSidebarSearch.bind(this);
    this.refreshMenus = this.refreshMenus.bind(this);
  }

  componentDidMount() {
    window.addEventListener("pondstat-scoped-account-changed", this.refreshMenus);
  }

  componentWillUnmount() {
    window.removeEventListener("pondstat-scoped-account-changed", this.refreshMenus);
  }

  refreshMenus() {
    this.baseMenus = menusVisibleForUser();
    this.setState({
      menus: this.baseMenus.map((item) => ({ ...item, search: false })),
      scopedAccount: getScopedAccount(),
    });
  }

  handleExpand(e, i, match) {
    e.preventDefault();

    if (this.state.clicked === -1 && match) {
      this.setState(() => ({
        active: -1,
        clicked: 1,
      }));
    } else {
      this.setState(() => ({
        active: this.state.active === i ? -1 : i,
        clicked: 1,
      }));
    }
  }

  handleLogout = (toggleAppSidebarMobile) => {
    if (window.innerWidth <= 768 && toggleAppSidebarMobile) {
      const syntheticEvent = {
        preventDefault: () => {},
        stopPropagation: () => {},
        target: null,
      };
      toggleAppSidebarMobile(syntheticEvent);
    }

    setTimeout(() => {
      clearStashedCompanySession();
      clearScopedAccount();
      clearAuthData();
      window.location.href = "/user/login";
    }, 200);
  };

  handleReturnToCompany = () => {
    if (isCompanyManagerUser() && isActingAsScopedAccount()) {
      clearScopedAccount();
      this.props.history.push("/company/accounts");
      this.refreshMenus();
      return;
    }
    if (!returnToCompanySession()) {
      clearStashedCompanySession();
    }
  };

  handleSidebarSearch(e) {
    let searchValue = e.target.value;
    searchValue = searchValue.toLowerCase();

    this.setState(() => {
      let newMenus = [];
      if (searchValue !== "") {
        newMenus = this.baseMenus.filter((item) => {
          let title = item.title;
          title = title.toLowerCase();
          if (title.search(searchValue) > -1) {
            item.search = true;
            return true;
          }
          if (item.children) {
            for (var i = 0; i < item.children.length; i++) {
              let title2 = item.children[i]["title"];
              title2 = title2.toLowerCase();

              if (title2.search(searchValue) > -1) {
                item.search = true;
                return true;
              }
            }
          }
          return false;
        });
      } else {
        newMenus = this.baseMenus.map((item) => ({ ...item, search: false }));
      }
      return {
        menus: newMenus,
      };
    });
  }

  renderSection(sectionKey, label, toggleAppSidebarMobile) {
    const sectionMenus = this.state.menus.filter(
      (m) => (m.section || "menu") === sectionKey
    );
    if (sectionMenus.length === 0 && sectionKey !== "general") return null;

    const showReturnToCompany =
      (hasStashedCompanySession() && !isCompanyManagerUser()) ||
      (isCompanyManagerUser() && isActingAsScopedAccount());

    const scoped = this.state.scopedAccount;
    const showScopedBanner =
      isActingAsScopedAccount() &&
      (isCompanyManagerUser() || isSuperUserSession());

    return (
      <React.Fragment key={sectionKey}>
        {sectionKey === "menu" && showScopedBanner && scoped ? (
          <div className="donezo-scoped-banner" title="Working on this account">
            <span className="donezo-scoped-banner__label">Working on</span>
            <span className="donezo-scoped-banner__name">
              {scoped.name || scoped.email || `Account #${scoped.id}`}
            </span>
          </div>
        ) : null}
        {sectionMenus.length > 0 ? (
          <div className={`menu-header${sectionKey === "general" ? " menu-header--section-divider" : ""}`}>
            {label}
          </div>
        ) : sectionKey === "general" ? (
          <div className="menu-header menu-header--section-divider">{label}</div>
        ) : null}
        {sectionMenus.map((menu, i) => {
          const globalIndex = this.state.menus.indexOf(menu);
          const menuRouteMatch = menuMatchesLocation(
            this.props.location.pathname,
            menu
          );
          return (
            <SidebarNavList
              data={menu}
              key={menu.path || i}
              expand={(e) => this.handleExpand(e, globalIndex, menuRouteMatch)}
              active={globalIndex === this.state.active}
              clicked={this.state.clicked}
            />
          );
        })}
        {sectionKey === "general" && (
          <>
            {showReturnToCompany && (
              <div
                className="menu-item"
                onClick={this.handleReturnToCompany}
                title="Back to company accounts"
              >
                <span className="menu-link">
                  <div className="menu-icon">
                    <i className="fa-solid fa-building"></i>
                  </div>
                  <div className="menu-text">
                    {isCompanyManagerUser() && isActingAsScopedAccount()
                      ? "Back to accounts"
                      : "Return to company"}
                  </div>
                </span>
              </div>
            )}
            <div
              className="menu-item donezo-logout-item"
              onClick={() => this.handleLogout(toggleAppSidebarMobile)}
            >
              <span className="menu-link">
                <div className="menu-icon">
                  <i className="fa-solid fa-right-from-bracket"></i>
                </div>
                <div className="menu-text">Logout</div>
              </span>
            </div>
          </>
        )}
      </React.Fragment>
    );
  }

  render() {
    return (
      <AppSettings.Consumer>
        {({ appSidebarSearch, toggleAppSidebarMobile }) => (
          <div className="menu">
            {appSidebarSearch && (
              <div className="menu-search mb-n3">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Sidebar menu filter..."
                  onKeyUp={this.handleSidebarSearch}
                />
              </div>
            )}
            {SECTIONS.map(({ key, label }) =>
              this.renderSection(key, label, toggleAppSidebarMobile)
            )}
          </div>
        )}
      </AppSettings.Consumer>
    );
  }
}

export default withRouter(SidebarNav);
